/**
 * FontPreloader - Sistema de pré-carregamento de fontes para PDF
 *
 * Carrega fontes em background durante a inicialização da aplicação
 * Reduz drasticamente o tempo de primeira geração de PDF
 *
 * Performance Impact:
 * - Sem preload: 2-5s de espera ao gerar primeiro PDF
 * - Com preload: 0-500ms (fontes já em cache)
 * - Redução: ~90% no tempo de inicialização do PDF
 */

import { FontCache } from './fontCache'
import { assertEmbeddableFont, FONT_FAMILIES, FONT_URLS, REFERENCE_FONT, type FontFamilyValue } from './fontOptions'

type FontFamily = FontFamilyValue

/**
 * As URLs vêm do registro único em fontOptions.ts. Antes este arquivo tinha
 * uma cópia manual da tabela de pdfLibGenerator.ts: quando uma das duas
 * mudava, o preloader aquecia o cache de uma URL e o gerador baixava outra.
 */

/**
 * Status do preload
 */
interface PreloadStatus {
  isPreloading: boolean
  preloadedFonts: Set<FontFamily>
  failedFonts: Set<FontFamily>
  startTime: number | null
  endTime: number | null
}

const preloadStatus: PreloadStatus = {
  isPreloading: false,
  preloadedFonts: new Set(),
  failedFonts: new Set(),
  startTime: null,
  endTime: null
}

/**
 * Pré-carrega uma única fonte (3 variantes em paralelo)
 */
async function preloadFont(fontFamily: FontFamily): Promise<void> {
  const urls = FONT_URLS[fontFamily]

  try {
    // Carregar as 3 variantes em paralelo
    const loadPromises = [
      loadFontFile(urls.regular),
      loadFontFile(urls.bold),
      loadFontFile(urls.italic)
    ]

    await Promise.all(loadPromises)

    preloadStatus.preloadedFonts.add(fontFamily)
  } catch (error) {
    preloadStatus.failedFonts.add(fontFamily)
    // Não rejeitar - continuar com outras fontes
  }
}

/**
 * Carrega um arquivo de fonte individual
 */
async function loadFontFile(url: string): Promise<ArrayBuffer> {
  // Verificar se já está em cache
  const cached = await FontCache.getFont(url)
  if (cached) {
    return cached
  }

  // Baixar do CDN
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} para ${url}`)
  }

  const fontBytes = await response.arrayBuffer()
  // Não envenenar o cache: o gerador leria estes bytes depois.
  assertEmbeddableFont(fontBytes, url)

  // Salvar no cache
  try {
    await FontCache.saveFont(url, fontBytes)
  } catch (cacheError) {
    // Continuar mesmo se cache falhar
  }

  return fontBytes
}

/**
 * Pré-carrega múltiplas fontes em paralelo
 * @param fonts - Array de famílias de fontes para pré-carregar
 */
export async function preloadFonts(fonts: FontFamily[]): Promise<void> {
  if (preloadStatus.isPreloading) {
    // Aguardar preload atual terminar
    while (preloadStatus.isPreloading) {
      await new Promise(resolve => setTimeout(resolve, 100))
    }
    return
  }

  preloadStatus.isPreloading = true
  preloadStatus.startTime = Date.now()

  try {
    // Carregar todas as fontes em paralelo
    await Promise.allSettled(fonts.map(font => preloadFont(font)))

    preloadStatus.endTime = Date.now()
  } finally {
    preloadStatus.isPreloading = false
  }
}

/**
 * Pré-carrega apenas a fonte padrão (Tinos — métricas do Times New Roman)
 * Use isso se quiser preload rápido com mínimo impacto
 */
export async function preloadDefaultFont(): Promise<void> {
  return preloadFonts([REFERENCE_FONT])
}

/**
 * Pré-carrega todas as fontes disponíveis
 * Use isso se o usuário troca frequentemente de fontes
 * ATENÇÃO: Irá baixar ~2-3MB de fontes
 */
export async function preloadAllFonts(): Promise<void> {
  return preloadFonts(FONT_FAMILIES.map(f => f.value))
}

/**
 * Pré-carrega as fontes mais comuns (padrão + DejaVu)
 * Bom equilíbrio entre cobertura e tamanho
 */
export async function preloadCommonFonts(): Promise<void> {
  return preloadFonts([REFERENCE_FONT, 'dejavu'])
}

/**
 * Verifica se uma fonte específica foi pré-carregada
 */
export function isFontPreloaded(font: FontFamily): boolean {
  return preloadStatus.preloadedFonts.has(font)
}

/**
 * Retorna status detalhado do preload
 */
export function getPreloadStatus(): PreloadStatus {
  return { ...preloadStatus }
}

/**
 * Retorna estatísticas do preload
 */
export function getPreloadStats(): {
  isPreloading: boolean
  totalPreloaded: number
  totalFailed: number
  duration: number | null
  preloadedFonts: FontFamily[]
  failedFonts: FontFamily[]
} {
  return {
    isPreloading: preloadStatus.isPreloading,
    totalPreloaded: preloadStatus.preloadedFonts.size,
    totalFailed: preloadStatus.failedFonts.size,
    duration: preloadStatus.startTime && preloadStatus.endTime
      ? preloadStatus.endTime - preloadStatus.startTime
      : null,
    preloadedFonts: Array.from(preloadStatus.preloadedFonts),
    failedFonts: Array.from(preloadStatus.failedFonts)
  }
}

/**
 * Limpa status do preload (útil para testes)
 */
export function resetPreloadStatus(): void {
  preloadStatus.isPreloading = false
  preloadStatus.preloadedFonts.clear()
  preloadStatus.failedFonts.clear()
  preloadStatus.startTime = null
  preloadStatus.endTime = null
}

/**
 * Hook React para iniciar preload automaticamente
 */
export function useFontPreload(
  fonts: FontFamily[] = [REFERENCE_FONT],
  options: { autoStart?: boolean; delay?: number } = {}
): {
  isLoading: boolean
  stats: ReturnType<typeof getPreloadStats>
  startPreload: () => Promise<void>
} {
  const { autoStart = true, delay = 2000 } = options

  const [isLoading, setIsLoading] = React.useState(false)
  const [stats, setStats] = React.useState(getPreloadStats())

  const startPreload = React.useCallback(async () => {
    setIsLoading(true)
    try {
      await preloadFonts(fonts)
    } finally {
      setIsLoading(false)
      setStats(getPreloadStats())
    }
  }, [fonts])

  React.useEffect(() => {
    if (autoStart) {
      // Aguardar um pouco antes de iniciar (não bloquear inicialização)
      const timer = setTimeout(() => {
        startPreload()
      }, delay)

      return () => clearTimeout(timer)
    }
  }, [autoStart, delay, startPreload])

  return { isLoading, stats, startPreload }
}

// TypeScript hack para React hooks (se não estiver disponível, ignorar)
const React = typeof window !== 'undefined' && (window as any).React
  ? (window as any).React
  : { useState: () => [null, () => {}], useEffect: () => {}, useCallback: (fn: any) => fn }

export default {
  preloadFonts,
  preloadDefaultFont,
  preloadAllFonts,
  preloadCommonFonts,
  isFontPreloaded,
  getPreloadStatus,
  getPreloadStats,
  resetPreloadStatus,
  useFontPreload
}
